import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import HowToCard from '../HowToCard';
import { useGoToSection } from '../howToLinks';
import { errorText, formatAmount, saveBlob } from '../format';
import InvestmentsImportModal from './InvestmentsImportModal';
import InvestmentsSettings from './InvestmentsSettings';

const { Text } = Typography;

const sumOf = (values) =>
  Object.values(values || {}).reduce((s, v) => s + (v || 0), 0);

/**
 * The Telekom group investments form of one company: months already sent to
 * the group stay as they were sent, the following ones come from the
 * fixed-asset postings, each account in the category row it is mapped to.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company whose ledger is used
 * @returns {JSX.Element} The panel
 */
export default function InvestmentsPanel({ companyId }) {
  const { t } = useTranslation('clientReports');
  const queryClient = useQueryClient();
  // The form goes out after the month closes, so last month is the default.
  const [period, setPeriod] = useState(
    dayjs().subtract(1, 'month').startOf('month')
  );
  const [importFile, setImportFile] = useState(null);
  const [picked, setPicked] = useState({});
  const year = period.year();
  const month = period.month() + 1;

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-reports-investments', companyId, year, month],
    queryFn: () => API.clientReports.getInvestments(companyId, { year, month }),
    retry: false,
  });
  const report = data?.data;
  const [openSections, setOpenSections] = useState([]);
  useGoToSection('investments', (key) => setOpenSections((keys) => [...new Set([...keys, key])]));

  const refresh = () => {
    queryClient.invalidateQueries({
      queryKey: ['client-reports-investments', companyId],
    });
    queryClient.invalidateQueries({
      queryKey: ['client-reports-investments-config', companyId],
    });
  };

  const exportMutation = useMutation({
    mutationFn: () =>
      API.clientReports.exportInvestments(companyId, { year, month }),
    onSuccess: saveBlob,
    onError: async (e) => message.error(await errorText(e)),
  });

  const unfreezeMutation = useMutation({
    mutationFn: (m) =>
      API.clientReports.unfreezeInvestmentMonth(companyId, { year, month: m }),
    onSuccess: () => {
      message.success(t('investments.unfrozen'));
      refresh();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  // An account the ledger has no row for, mapped straight from the warning.
  const mapMutation = useMutation({
    mutationFn: ({ account, row }) =>
      API.clientReports.saveInvestmentConfig(companyId, {
        mapping: [...(report?.rules?.mapping || []), { account, row }],
      }),
    onSuccess: (_, { account }) => {
      message.success(t('investments.mapped', { account }));
      setPicked((p) => ({ ...p, [account]: undefined }));
      refresh();
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const rowOptions = useMemo(
    () => (report?.rows || []).map((r) => ({ value: r.key, label: r.label })),
    [report]
  );
  const labelOf = useMemo(
    () => new Map((report?.rows || []).map((r) => [r.key, r.label])),
    [report]
  );

  // Rows with an amount in any month, or with an account mapped to them.
  const rows = useMemo(() => {
    if (!report) return [];
    const used = new Set(report.rules.mapping.map((mp) => mp.row));
    report.months.forEach((mo) =>
      Object.entries(mo.values).forEach(([k, v]) => {
        if (Math.abs(v) >= 0.005) used.add(k);
      })
    );
    return [
      ...report.rows
        .filter((r) => used.has(r.key))
        .map((r) => ({ key: r.key, label: r.label })),
      { key: '__total', label: t('investments.total'), summary: true },
    ];
  }, [report, t]);

  const unmapped = useMemo(() => {
    const byAccount = {};
    (report?.months || []).forEach((mo) =>
      Object.entries(mo.unmapped).forEach(([account, amount]) => {
        if (!byAccount[account]) byAccount[account] = { account, months: [] };
        byAccount[account].months.push({ month: mo.month, amount });
      })
    );
    return Object.values(byAccount);
  }, [report]);

  const cellFor = (row, mo) => {
    if (row.summary) return <Text strong>{formatAmount(sumOf(mo.values))}</Text>;
    const value = mo.values[row.key] || 0;
    const ledger = mo.computed[row.key] || 0;
    const differs = mo.frozen && Math.abs(value - ledger) >= 0.01;
    const text = Math.abs(value) >= 0.005 ? formatAmount(value) : '';
    if (!differs) return text;
    return (
      <Tooltip title={t('investments.ledgerValue', { amount: formatAmount(ledger) })}>
        <span style={{ borderBottom: '1px dashed #d48806' }}>{text || '0,00'}</span>
      </Tooltip>
    );
  };

  const columns = useMemo(() => {
    if (!report) return [];
    return [
      {
        title: t('investments.row'),
        dataIndex: 'label',
        key: 'label',
        fixed: 'left',
        width: 340,
        render: (label, row) => (row.summary ? <Text strong>{label}</Text> : label),
      },
      ...report.months.map((mo) => ({
        key: `m${mo.month}`,
        align: 'right',
        width: 140,
        title: (
          <Space direction="vertical" size={2} style={{ alignItems: 'flex-end' }}>
            <span>{`${String(mo.month).padStart(2, '0')}.${year}`}</span>
            {mo.frozen ? (
              <Space size={4}>
                <Tooltip
                  title={t('investments.frozenAt', {
                    date: dayjs(mo.frozen.frozenAt).format('DD.MM.YYYY HH:mm'),
                  })}
                >
                  <Tag
                    color={mo.frozen.source === 'upload' ? 'purple' : 'blue'}
                    style={{ marginInlineEnd: 0 }}
                  >
                    {t(`investments.frozen.${mo.frozen.source}`)}
                  </Tag>
                </Tooltip>
                <Popconfirm
                  title={t('investments.unfreezeConfirm')}
                  okText={t('investments.unfreeze')}
                  cancelText={t('fccs.cancel')}
                  onConfirm={() => unfreezeMutation.mutate(mo.month)}
                >
                  <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}>
                    {t('investments.unfreeze')}
                  </Button>
                </Popconfirm>
              </Space>
            ) : (
              <Tag style={{ marginInlineEnd: 0 }}>{t('investments.fromLedger')}</Tag>
            )}
          </Space>
        ),
        render: (_, row) => cellFor(row, mo),
      })),
      {
        key: 'sum',
        align: 'right',
        width: 150,
        title: t('investments.yearTotal'),
        render: (_, row) => {
          const total = report.months.reduce(
            (s, mo) => s + (row.summary ? sumOf(mo.values) : mo.values[row.key] || 0),
            0
          );
          return <Text strong>{formatAmount(total)}</Text>;
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, t, year]);

  const lineColumns = [
    {
      title: t('investments.date'),
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 100,
      render: (d) => dayjs(d).format('DD.MM.YYYY'),
    },
    { title: t('investments.order'), dataIndex: 'orderNo', key: 'orderNo', width: 100 },
    {
      title: t('investments.account'),
      dataIndex: 'account',
      key: 'account',
      render: (a) => (
        <span>
          {a}
          {report?.accountNames?.[a] && (
            <Text type="secondary">{` ${report.accountNames[a]}`}</Text>
          )}
        </span>
      ),
    },
    { title: t('investments.description'), dataIndex: 'description', key: 'description' },
    { title: t('investments.invoice'), dataIndex: 'invoiceNo', key: 'invoiceNo', width: 170 },
    {
      title: t('investments.amount'),
      dataIndex: 'debit',
      key: 'debit',
      align: 'right',
      width: 140,
      render: formatAmount,
    },
    {
      title: t('investments.row'),
      dataIndex: 'row',
      key: 'row',
      render: (row) =>
        row ? labelOf.get(row) || row : <Tag color="orange">{t('investments.noRow')}</Tag>,
    },
  ];

  // 404 = nothing has arrived from AB for that year yet.
  const errorMessage =
    error?.response?.status === 404
      ? t('fccs.noLedger', { year })
      : error?.response?.data?.message || error?.message;

  return (
    <div>
      <HowToCard guide="investments" open={report ? !report.template || !report.configured : undefined} />
      <Space id="howto-investments-toolbar" wrap style={{ display: 'flex', marginBottom: 12 }}>
        <DatePicker
          picker="month"
          allowClear={false}
          value={period}
          format="MM.YYYY"
          onChange={(value) => value && setPeriod(value.startOf('month'))}
        />
        <Button
          type="primary"
          className="btn-primary"
          icon={<DownloadOutlined />}
          disabled={!report?.template}
          loading={exportMutation.isPending}
          onClick={() => exportMutation.mutate()}
        >
          {t('investments.download')}
        </Button>
        <Upload
          accept=".xlsx"
          showUploadList={false}
          beforeUpload={(file) => {
            setImportFile(file);
            return false;
          }}
        >
          <Button shape="round" icon={<UploadOutlined />}>
            {t('investments.importButton')}
          </Button>
        </Upload>
      </Space>

      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
        {t('investments.hint')}
      </div>

      {errorMessage && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={errorMessage} />
      )}
      {report && !report.template && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('investments.noTemplate')} />
      )}
      {report?.template && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
          {t('investments.templateInfo', {
            file: report.template.fileName || report.template.sheetName,
            date: dayjs(report.template.uploadedAt).format('DD.MM.YYYY'),
          })}
        </Text>
      )}
      {report?.template && !report.configured && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('investments.notConfigured')} />
      )}

      {report?.template && unmapped.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('investments.unmappedTitle')}
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              {unmapped.map((u) => (
                <Space key={u.account} wrap>
                  <Text strong>{u.account}</Text>
                  {report.accountNames?.[u.account] && (
                    <Text type="secondary">{report.accountNames[u.account]}</Text>
                  )}
                  <Text>
                    {u.months
                      .map((mo) => `${String(mo.month).padStart(2, '0')}.${year}: ${formatAmount(mo.amount)}`)
                      .join(' · ')}
                  </Text>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    style={{ width: 380 }}
                    placeholder={t('investments.pickRow')}
                    options={rowOptions}
                    value={picked[u.account]}
                    onChange={(row) => setPicked((p) => ({ ...p, [u.account]: row }))}
                  />
                  <Button
                    disabled={!picked[u.account]}
                    loading={mapMutation.isPending && mapMutation.variables?.account === u.account}
                    onClick={() => mapMutation.mutate({ account: u.account, row: picked[u.account] })}
                  >
                    {t('investments.mapAccount')}
                  </Button>
                </Space>
              ))}
            </Space>
          }
        />
      )}

      {report?.template && (
        <Table
          scroll={{ x: 'max-content' }}
          rowKey="key"
          size="small"
          pagination={false}
          loading={isLoading}
          columns={columns}
          dataSource={rows}
        />
      )}

      <Collapse
        style={{ marginTop: 16 }}
        activeKey={openSections}
        onChange={setOpenSections}
        items={[
          ...(report
            ? [
              {
                key: 'lines',
                label: t('investments.linesTitle', {
                  period: `${String(month).padStart(2, '0')}.${year}`,
                  count: report.lines.length,
                }),
                children: (
                  <Table
                    size="small"
                    rowKey={(l, i) => `${l.orderNo}-${i}`}
                    pagination={false}
                    columns={lineColumns}
                    dataSource={report.lines}
                    locale={{ emptyText: t('investments.noLines') }}
                  />
                ),
              },
            ]
            : []),
          {
            key: 'settings',
            label: t('investments.settingsTitle'),
            children: (
              <div id="howto-investments-settings">
                <InvestmentsSettings companyId={companyId} onSaved={refresh} />
              </div>
            ),
          },
        ]}
      />

      {importFile && (
        <InvestmentsImportModal
          companyId={companyId}
          file={importFile}
          year={year}
          onClose={() => setImportFile(null)}
          onApplied={() => {
            setImportFile(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
